import { create } from 'zustand';
import { fetchLoginNotifications, type LoginNotification } from '@/api/uiConfig';
import { markSeen } from '@/lib/notificationSeen';
import { isFeature } from '@/clients';

type LoginNotificationsState = {
  /** Announcements still to show, newest first (the order the API returns). */
  queue: LoginNotification[];
  /** Drops the notice currently on screen and reveals the next one. */
  dismissFirst: () => void;
  clear: () => void;
};

/**
 * Sign-in announcements awaiting display.
 *
 * In memory only, never persisted: dismissal is session-local by design —
 * nothing is written server-side, and a login notice has no read state to
 * store anywhere.
 */
export const useLoginNotificationsStore = create<LoginNotificationsState>((set) => ({
  queue: [],
  dismissFirst: () => set((s) => ({ queue: s.queue.slice(1) })),
  clear: () => set({ queue: [] }),
}));

/**
 * Loads the announcements for a session that has just started.
 *
 * Call immediately after a successful login or register, once, and nowhere
 * else. Must run AFTER `setSession` — the request carries the bearer token
 * from the auth store.
 *
 * Never throws: an announcement that fails to load must not disturb a
 * successful sign-in.
 */
export async function loadLoginNotifications(): Promise<void> {
  // A client with announcements switched off never asks for them. Gated here
  // rather than at the five auth-success call sites, so no later sign-in path
  // can reintroduce the request by forgetting the check.
  if (!isFeature('notifications')) return;
  try {
    const list = await fetchLoginNotifications();
    if (list.length === 0) return;
    // The ordinary feed can hold the same notice (a notification flipped to
    // is_login keeps its per-user rows). Claim the ids now so the Home popup
    // doesn't show it a second time.
    markSeen(...list.map((n) => n.id));
    useLoginNotificationsStore.setState({ queue: list });
  } catch {
    // Sign-in already succeeded — swallow and show nothing.
  }
}
